import { ANIMATABLE, AXES, AXIS_TRACKS, CURVE_PRESETS, EASES, axisTracksPresent, copyValue, evalTrack, hasKeyAt, isCurve, removeKey, setKey } from '../core/anim.js';
import { select } from '../scene/objects.js';
import { on, requestRender } from '../state/bus.js';
import { pushHistory } from '../state/history.js';
import {
  addTrack, applyAnimation, captureKey, frameCount, frameDur, pause, quantise,
  combineProp, readProp, removeTrack, setPlayhead, snapKeysToFrames, splitProp, toFrame, togglePlay
} from '../state/timeline.js';
import { MOTION_CTA, canUseMotion } from '../services/limits.js';
import { showTrial } from './trial.js';
import { $, S, clamp, el, fieldOwnsKey, typingTarget } from '../state/store.js';
import { renderRight } from './rail-right.js';
import { toast } from './toast.js';

/* THE TIMELINE DOCK — the strip along the bottom.

   Two columns that scroll as one: property names on the left, a lane per
   property on the right with a diamond per keyframe. The same arrangement
   every editing tool has used since the first one, and worth copying exactly
   rather than inventing — the whole value of the layout is that people
   already know how to read it.

   Only objects with at least one track get a row. A scene with fifty objects
   and two animated ones shows two, not fifty greyed-out invitations. The way
   IN is the stopwatch beside each property in the inspector (see stopwatch()
   at the bottom of this file), which is where the person already is when they
   decide something should move.

   Rebuilding vs updating: the rows are rebuilt wholesale whenever the set of
   tracks changes, same posture as the inspector — at this size it is cheaper
   than diffing and it cannot go stale. Two things are deliberately NOT done
   that way, because both happen at pointer or frame rate: the playhead, which
   moves sixty times a second during playback, and a keyframe drag, which
   moves the elements it is dragging directly. Rebuilding during a drag
   detaches the very element the pointer is holding. */

const COLLAPSE_KEY = 'illusio3d.timeline-open';
const RESIZE_KEY = 'illusio3d.timeline-height';
const MIN_BODY_H = 100;           // enough for the ruler and one row before it stops being useful
const LANE_PAD = 10;              // px of slack at each end, so a key at t=0 is grabbable

/* The selection is a LIST. Shift-click adds one, dragging a box over the
   lanes takes everything inside it, and a drag moves all of them together —
   which is the whole reason multi-select exists: retiming a move means
   shifting position, rotation and scale by the same amount, not one at a
   time and hoping they still line up. */
let sel = [];                     // [{o, prop, t}]

/* Object ids whose property rows are folded away. Their keyframes do not
   vanish with them — they merge onto the object's own row, where they can
   still be selected and dragged as a group. A collapsed object you cannot
   retime would just be a worse version of scrolling past it. */
const collapsed = new Set();
let lanesEl = null;

/* ── time ↔ pixels ───────────────────────────────────────────────────────── */

/* 1 = the whole duration fits the visible strip, same as before zoom existed.
   Past that, laneWidth() outgrows the viewport and #tlLanes's own
   overflow-x:auto is what makes the extra width reachable — panning is a
   trackpad swipe or Shift+wheel, zooming is a plain wheel or the toolbar
   buttons (wired in initTimeline()). 20x puts a 5s piece at a frame every
   ~8px at 30fps, plenty to grab a single frame by hand. */
let zoom = 1;
const MIN_ZOOM = 1, MAX_ZOOM = 20;

const viewportWidth = () => Math.max(1, (lanesEl?.clientWidth || 600) - LANE_PAD * 2);
const laneWidth = () => viewportWidth() * zoom;
const tToX = t => LANE_PAD + (t / S.timeline.duration) * laneWidth();
const xToT = x => clamp((x - LANE_PAD) / laneWidth(), 0, 1) * S.timeline.duration;
const pxPerFrame = () => laneWidth() / frameCount();

/* Change zoom while holding one point in time fixed under one pixel — the
   cursor while scrolling the wheel, the middle of the visible strip for the
   toolbar buttons, which have no cursor position of their own to anchor to.
   Without this every zoom step also silently pans, which reads as the
   timeline lurching around rather than the intended point growing in place. */
function setZoom(next, anchorT, anchorPx) {
  const nextZoom = clamp(next, MIN_ZOOM, MAX_ZOOM);
  if (nextZoom === zoom) return;
  if (lanesEl && anchorT == null) {
    anchorPx = viewportWidth() / 2;
    anchorT = xToT(lanesEl.scrollLeft + anchorPx);
  }
  zoom = nextZoom;
  renderTimeline();
  if (lanesEl && anchorT != null) lanesEl.scrollLeft = tToX(anchorT) - anchorPx;
}

/* m:ss:ff — the last pair is FRAMES, not hundredths.

   The export is frames, so the authoring readout is frames: "1:12" is a
   position someone can hold you to, where "1.40s" is a number that gets
   rounded to a frame later by machinery they cannot see. Padded to the width
   of the frame count so the digits do not shuffle sideways as it plays. */
function fmtTime(t) {
  const fps = S.timeline.fps, f = toFrame(t);
  const secs = Math.floor(f / fps);
  const pad = String(fps - 1).length;
  return `${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, '0')}:${String(f % fps).padStart(pad, '0')}`;
}

/* ── the ruler ───────────────────────────────────────────────────────────── */

function buildRuler(ruler) {
  ruler.innerHTML = '';
  const dur = S.timeline.duration;
  /* One label per second up to ten, then per five — past that they collide,
     and an unreadable ruler is worse than a less precise one. */
  const step = dur <= 10 ? 1 : dur <= 30 ? 5 : 10;

  /* Individual frames, but only where there is room for them to mean
     something. Closer than four pixels they stop being ticks and become a
     grey wash, so below that they are simply not drawn. */
  if (pxPerFrame() >= 4)
    for (let f = 0; f <= frameCount(); f++) {
      if (f % S.timeline.fps === 0) continue;         // the second lines are drawn below
      const t = el('div', 'tltick tltick--frame');
      t.style.left = tToX(f * frameDur()) + 'px';
      ruler.appendChild(t);
    }

  for (let s = 0; s <= dur + 1e-9; s += step) {
    const tick = el('div', 'tltick');
    tick.style.left = tToX(s) + 'px';
    const label = el('span', null, (s % 1 ? s.toFixed(1) : String(s)) + 's');
    /* The final tick sits at the very right edge, so its label would hang off
       the end and be clipped. Flip it to the inside for that one. */
    if (s > dur - step * .5) label.classList.add('tltick__end');
    tick.appendChild(label);
    ruler.appendChild(tick);
  }
}

/* ── keyframe shapes ───────────────────────────────────────────────────────
   After Effects draws the interpolation into the keyframe itself, and it is
   worth copying because it makes timing legible without clicking anything: a
   diamond is linear, an hourglass eases, a square holds.

   Ours carries one ease per keyframe, describing the segment LEAVING it, so
   the shape is split down the middle — left half for how the value arrives,
   right half for how it departs. The previous version rotated a CSS square by
   45°, which could only ever be one shape for all five. */
const D = 11;
const HALF_DIAMOND_L = `M${D / 2} 0 L${D / 2} ${D} L0 ${D / 2} Z`;
const HALF_DIAMOND_R = `M${D / 2} 0 L${D} ${D / 2} L${D / 2} ${D} Z`;
const HALF_EASE_L = `M0 0 L${D / 2} 0 L${D / 2} ${D} L0 ${D} L${D / 2} ${D / 2} Z`;
const HALF_EASE_R = `M${D} 0 L${D / 2} 0 L${D / 2} ${D} L${D} ${D} L${D / 2} ${D / 2} Z`;

/* The three overshooting eases are new, and unlike the others there is no
   familiar icon to copy — After Effects has no bounce. They take the 'out'
   silhouette, because that is how they depart, and are told apart by one, two
   or three holes punched through the departing half, die-face fashion: one
   pass at the target, a couple, several. The menu names them in words. */
const OVERSHOOT_DOTS = { back: [[7.6, 5.5]], bounce: [[7.6, 4.1], [7.6, 6.9]],
                         elastic: [[7.6, 3.2], [7.6, 5.5], [7.6, 7.8]] };
const dotHoles = e => (OVERSHOOT_DOTS[e] || [])
  .map(([x, y]) => `M${x} ${y} m-1.1 0 a1.1 1.1 0 1 0 2.2 0 a1.1 1.1 0 1 0 -2.2 0`).join(' ');

const FULL_DIAMOND = `M${D / 2} 0 L${D} ${D / 2} L${D / 2} ${D} L0 ${D / 2} Z`;

/** What an ease is called, in a sentence. */
export const easeLabel = e => (isCurve(e) ? 'Custom curve' : e);

function keyShape(ease) {
  /* A CUSTOM CURVE gets a whole diamond with a hole through the middle. The
     other eight silhouettes work because they are already familiar from other
     tools, and a custom curve has no such icon — drawing the real curve at
     11px is mush. So: a diamond, visibly modified, implying no direction the
     curve may not have. */
  if (isCurve(ease))
    return `<path fill-rule="evenodd" d="${FULL_DIAMOND} `
         + `M${D / 2} ${D / 2} m-1.9 0 a1.9 1.9 0 1 0 3.8 0 a1.9 1.9 0 1 0 -3.8 0"/>`;
  /* A circle, which is what After Effects draws for auto bezier — worth
     copying exactly, since the whole value of these shapes is that they are
     already familiar. It reads as "no corner here": the curve runs through
     this key rather than turning at it. */
  if (ease === 'auto') return `<circle cx="${D / 2}" cy="${D / 2}" r="${D / 2 - 0.6}"/>`;
  if (ease === 'hold') return `<rect x="0.5" y="0.5" width="${D - 1}" height="${D - 1}" rx="1"/>`;
  /* Holes rather than added marks: the box is 11px and every corner outside
     the diamond is already spoken for by one silhouette or another, so the
     only free space left is inside the shape itself. */
  if (OVERSHOOT_DOTS[ease])
    return `<path d="${HALF_EASE_L}"/>`
         + `<path fill-rule="evenodd" d="${HALF_DIAMOND_R} ${dotHoles(ease)}"/>`;
  const right = ease === 'linear' || ease === 'out' ? HALF_DIAMOND_R : HALF_EASE_R;
  const left = ease === 'inout' || ease === 'out' ? HALF_EASE_L : HALF_DIAMOND_L;
  return `<path d="${left}"/><path d="${right}"/>`;
}

/* ── selection ───────────────────────────────────────────────────────────── */

const sameKey = (a, b) => a.o === b.o && a.prop === b.prop && Math.abs(a.t - b.t) < 1e-6;
const isSelected = k => sel.some(s => sameKey(s, k));

function addToSelection(list) {
  for (const k of list) if (!isSelected(k)) sel.push({ ...k });
}

/** Repaint which diamonds look selected, without rebuilding any rows. */
function paintSelection() {
  for (const n of document.querySelectorAll('.tlkey'))
    n.classList.toggle('on', (n.__keys || []).some(isSelected));
  /* The curve panel is about the selected keyframe, so it follows the
     selection rather than waiting for the next full rebuild. */
  renderCurvePanel();
}

/* ── dragging keyframes ──────────────────────────────────────────────────── */

/* How close a drop has to land to another keyframe in the SAME track to
   count as "on top of it" and replace it (see replaceOnDrop, below) — half a
   frame at 120fps, comfortably finer than any real project's own frame rate,
   so quantise() landing two keyframes on the identical displayed frame
   always falls inside this. */
const MIN_GAP = 1 / 120;

/* Snapping.

   Within SNAP_PX of another keyframe, the playhead, or either end of the
   timeline, a dragged keyframe lands exactly on it. In PIXELS rather than in
   seconds, deliberately: the useful question is "is it close enough to look
   aligned", which is a question about the screen, and a fixed time tolerance
   would feel sticky on a 30-second timeline and useless on a 2-second one.

   Nothing in a track that is MOVING is a target — snapping onto a key you are
   also dragging is meaningless, and snapping onto a stationary neighbour in
   your own track would merge the two. Hold Alt to place freely. */
const SNAP_PX = 7;

function snapDelta(moves, dt) {
  const tol = (SNAP_PX / laneWidth()) * S.timeline.duration;
  /* Excludes only the specific keyframes actually being dragged (matched by
     their CURRENT, live-dragged position — not their original t0, since the
     array already holds this frame's applied position by the time this
     runs). Everything else in the same track is now a valid target on
     purpose: snapping onto a stationary sibling is what makes it easy to
     land exactly on top of one and replace it. */
  const isMoving = (id, t) => moves.some(m => (m.o.id + ':' + m.prop) === id && Math.abs(m.last - t) < 1e-6);
  const targets = [0, S.timeline.duration, S.playhead];
  for (const ob of S.objects)
    for (const [p, keys] of Object.entries(ob.anim || {})) {
      const id = ob.id + ':' + p;
      for (const k of keys) if (!isMoving(id, k.t)) targets.push(k.t);
    }

  let best = dt, bestD = tol;
  for (const m of moves)
    for (const c of targets) {
      const d = Math.abs(m.t0 + dt - c);
      if (d < bestD) { bestD = d; best = c - m.t0; }
    }
  return best;
}

/* Only the timeline's own outer bounds — a keyframe can't sit before 0 or
   after S.timeline.duration. Neighbours within the same track are no longer
   a limit here: dragging one onto another now REPLACES it (replaceOnDrop,
   below) rather than being stopped short of it, which is the more useful
   reading of "I dropped it there" than silently refusing the last pixel of
   the drag. */
function clampDelta(moves, dt) {
  let lo = -Infinity, hi = Infinity;
  for (const m of moves) {
    lo = Math.max(lo, -m.t0);
    hi = Math.min(hi, S.timeline.duration - m.t0);
  }
  return clamp(dt, lo, hi);
}

/* On drop, a keyframe landing on top of another one in the SAME track
   REPLACES it — the moved keyframe's value and easing win, the one it
   landed on is gone. This is the "set (or replace) the keyframe at t" rule
   core/anim.js's setKey already applies everywhere a keyframe gets written;
   dragging is just another way of writing one.

   Deliberately NOT done live, on every pointermove — during the drag the
   moved and the about-to-be-replaced keyframe simply sit on top of each
   other in the array, which is harmless (nothing reads the array as
   "exactly one entry per instant" until this runs) and cheap. Doing the
   actual removal only once, here, means a drag that passes back OFF a
   collision and settles somewhere else never touches the keyframe it
   grazed on the way past.

   The moved keyframe's ORIGINAL value/ease (captured once, at drag start —
   see startKeyDrag) is what gets written back, not whatever the live drag
   left sitting in the array at that spot, so the winner is unambiguous even
   when both a moved and a stationary entry are momentarily at the same
   time. */
function replaceOnDrop(moves) {
  const byTrack = new Map();
  for (const m of moves) {
    const id = m.o.id + ':' + m.prop;
    if (!byTrack.has(id)) byTrack.set(id, { o: m.o, prop: m.prop, landed: [] });
    byTrack.get(id).landed.push({ t: m.last, v: m.v, ease: m.ease });
  }
  for (const { o, prop, landed } of byTrack.values()) {
    let track = o.anim[prop];
    if (!track) continue;
    // Clear anything sitting at (or within MIN_GAP of) a landed time — the
    // moved keyframe's own stale live entry AND any stationary keyframe it
    // came to rest on top of.
    track = track.filter(k => !landed.some(l => Math.abs(k.t - l.t) < MIN_GAP));
    // Put the moved keyframes back, fresh, at their exact final positions.
    for (const l of landed) track = setKey(track, l.t, l.v, l.ease);
    o.anim[prop] = track;
  }
}

/** Put every keyframe in `moves` at its original time plus `dt`.

    Absolute target times, not a chain of relative nudges. Applying deltas
    frame by frame across a long drag lets quantisation compound, so a
    keyframe can end up a frame off from where the readout says it is — and
    the error only shows up after the drag, which is the worst time to find
    it. Recomputing from t0 every move cannot drift.

    Written into each keyframe's ORIGINAL OBJECT (moves[].ref, captured once
    in startKeyDrag) rather than found by matching the array for whichever
    entry currently sits at the last-known time. That value-matching version
    used to work only because dragging could never reach another keyframe in
    the same track — clampDelta stopped it short. Now that a drop is allowed
    to land ON one (see replaceOnDrop), a drag legitimately passes THROUGH a
    stationary keyframe's exact position on the way past it, and at that
    instant two array entries share one time value — a search by time can no
    longer tell which is the one actually being dragged, and could silently
    start moving the wrong one. A direct reference never has that ambiguity:
    it is always the same object, however many other keyframes it grazes. */
function applyDelta(moves, dt) {
  const touched = new Set();
  for (const m of moves) {
    if (!m.ref) continue;
    m.ref.t = Math.max(0, quantise(m.t0 + dt));
    touched.add(m.o.id + ':' + m.prop);
  }
  // Re-sort once per affected track, not once per keyframe — a multi-select
  // drag can move several keyframes out of the same track together.
  for (const id of touched) {
    const m = moves.find(x => (x.o.id + ':' + x.prop) === id);
    m.o.anim[m.prop] = [...m.o.anim[m.prop]].sort((a, b) => a.t - b.t);
  }

  for (const m of moves) { m.last = m.ref ? m.ref.t : Math.max(0, quantise(m.t0 + dt)); m.appliedDt = dt; }
  /* Keep the selection pointing at where the keyframes now ARE, so a second
     drag, a delete or an easing change finds them. */
  sel = moves.map(m => ({ o: m.o, prop: m.prop, t: m.last }));
}

function startKeyDrag(ev, node, grabbed) {
  ev.stopPropagation();
  ev.preventDefault();
  node.setPointerCapture(ev.pointerId);

  /* Measured ONCE, here. Reading it per pointermove is what made a keyframe
     leap on first touch: selecting the object rebuilt the dock, which
     detached the element, and its rect then came back as zeros — so every
     position was computed from the window's left edge instead of the lane's.
     The grab offset is kept for the same class of reason: without it the
     diamond teleports its centre to the cursor. */
  const laneRect = node.parentElement.getBoundingClientRect();
  const anchor = grabbed[0].t;
  const grabDx = ev.clientX - (laneRect.left + tToX(anchor));
  const moves = sel.map(s => {
    const key = (s.o.anim[s.prop] || []).find(k => Math.abs(k.t - s.t) < 1e-6);
    // v/ease are captured now, by VALUE, because replaceOnDrop needs to know
    // what this keyframe originally said even after ref.t has since moved —
    // ref itself is what applyDelta mutates directly, every frame of the drag.
    return { o: s.o, prop: s.prop, t0: s.t, last: s.t, appliedDt: 0, v: key?.v, ease: key?.ease, ref: key };
  });
  if (!moves.length) return;
  const readout = $('#tlDrag');
  let moved = false;

  const move = e => {
    /* Dragging moves keyframes in TIME only. Values are untouched — changing
       a value is what the viewport is for, and a lane that did both depending
       on which axis you happened to move in would be a trap. */
    let dt = xToT(e.clientX - grabDx - laneRect.left) - anchor;
    if (!e.altKey) dt = snapDelta(moves, dt);
    dt = quantise(clampDelta(moves, quantise(dt)));
    if (Math.abs(dt - moves[0].appliedDt) < 1e-9) return;
    applyDelta(moves, dt);
    moved = true;
    /* Move the elements, do not rebuild the dock — one style write each, and
       it keeps the dragged element alive under the pointer. */
    for (const m of moves) {
      const n = [...document.querySelectorAll('.tlkey')]
        .find(x => (x.__keys || []).some(k => k.o === m.o && k.prop === m.prop && Math.abs(k.t - m.t0) < 1e-6));
      if (n) n.style.left = tToX(m.last) + 'px';
    }
    applyAnimation();
    if (readout) {
      readout.textContent = (dt >= 0 ? '+' : '−') + Math.abs(toFrame(dt)) + ' frames';
      readout.classList.remove('hidden');
    }
  };
  const up = e => {
    node.releasePointerCapture?.(e.pointerId);
    node.removeEventListener('pointermove', move);
    node.removeEventListener('pointerup', up);
    readout?.classList.add('hidden');
    /* Selecting the object is a fine thing to do on touching its keyframe —
       just not before the drag, where the rebuild it triggers pulls the
       dragged element out from under the pointer. */
    select(grabbed[0].o.id);
    if (moved) { replaceOnDrop(moves); pushHistory(); }
    renderTimeline();
  };
  node.addEventListener('pointermove', move);
  node.addEventListener('pointerup', up);
}

/* ── rows ────────────────────────────────────────────────────────────────── */

/* Written with concatenation rather than a nested template literal. The two
   are equivalent to the engine, but tools/undefined-refs.mjs tokenises
   template strings without handling one nested inside a ${} — it read the
   inner words as identifiers and reported them unresolved. A tool that has to
   be worked around often should be fixed; this is the codebase's only nested
   template, so the flat form is the cheaper answer, and it reads better. */
function titleFor(t, ease, n, span = 0) {
  /* A diamond standing for several keys is worth saying out loud, and the two
     reasons it can happen read differently: they genuinely share an instant,
     or they are merely too close to draw apart at this zoom. The second one
     has a remedy, so it names it — otherwise somebody trying to reach one
     key of a cluster has no way of knowing there is one. */
  const frames = Math.round(span * S.timeline.fps);
  const many = n > 1
    ? (frames > 0
        ? ' · ' + n + ' keyframes within ' + frames + (frames === 1 ? ' frame' : ' frames')
          + ' — zoom in to separate them'
        : ' · ' + n + ' keyframes at this frame')
    : '';
  return fmtTime(t) + ' · ' + easeLabel(ease) + many + '\n' +
    'Drag to retime (Alt ignores snapping) · Shift-click to add to the selection · ' +
    'double- or right-click for easing and delete';
}

/**
 * One diamond. `group` is the keyframes it stands for — a single one, or all
 * of an object's keyframes at that instant when its rows are folded away.
 */
function keyframeEl(group, ease, span = 0) {
  const d = el('div', 'tlkey' + (group.some(isSelected) ? ' on' : ''));
  /* At the EARLIEST of the cluster, not its middle: startKeyDrag anchors on
     group[0].t, and a mark drawn anywhere else would hand the drag a grab
     offset measured from a place the diamond is not. */
  d.style.left = tToX(group[0].t) + 'px';
  d.innerHTML = `<svg viewBox="0 0 ${D} ${D}">${keyShape(ease)}</svg>`;
  if (group.length > 1) d.classList.add('tlkey--multi');
  d.title = titleFor(group[0].t, ease, group.length, span);
  /* The keyframes this element represents, kept on the node so the marquee
     and the drag can go from a hit element straight to model entries without
     re-deriving them from pixel positions. */
  d.__keys = group;

  d.addEventListener('pointerdown', ev => {
    if (ev.button !== 0) return;
    if (ev.shiftKey) addToSelection(group);
    else if (!group.some(isSelected)) sel = group.map(g => ({ ...g }));
    paintSelection();
    startKeyDrag(ev, d, group);
  });

  /* Two ways to the menu, plus the Delete key, because "how do I delete a
     keyframe" was a fair question to have to ask. */
  d.addEventListener('contextmenu', ev => { ev.preventDefault(); ev.stopPropagation(); openKeyMenu(d, group, ease); });
  d.addEventListener('dblclick', ev => { ev.stopPropagation(); openKeyMenu(d, group, ease); });
  return d;
}

/* ── the easing curve ──────────────────────────────────────────────────────

   A keyframe's ease is either one of nine names or four numbers — the two
   control points of a cubic bezier, exactly as CSS spells it (see
   core/anim.js). This is where the four numbers get dragged.

   A MODE OF THE DOCK rather than a dialog. Easing is read against the
   keyframes either side of it, so a panel covering them would hide the thing
   being judged. It sits in its own column beside the lanes, which also means
   the handles stay put while the lanes scroll under them.

   The plot is square and both axes run 0..1 — x is the segment's own time,
   NOT the timeline's. That is the whole reason this is not drawn into the
   lanes: there, one horizontal pixel already means an instant of the
   timeline, and a curve drawn in the same space would make it mean two
   things at once. */
const CURVE_KEY = 'illusio3d.timeline-curve';
let curveOpen = false;
try { curveOpen = localStorage.getItem(CURVE_KEY) === '1'; } catch (_) { /* private mode */ }

/* How far past the unit box a handle may be dragged. Overshoot is the point
   of dragging y outside [0,1] at all, and a ceiling keeps the plot readable:
   past about this much the curve is a spike and the box it is drawn in tells
   you nothing. */
const CURVE_Y = 1;
const PLOT = 100;                       // the SVG's own units; CSS sizes it

/** The keyframe the panel is editing, and the one after it. */
function curveSubject() {
  if (!sel.length) return null;
  const s = sel[0];
  const track = s.o.anim?.[s.prop];
  if (!track) return null;
  const i = track.findIndex(k => Math.abs(k.t - s.t) < 1e-6);
  if (i < 0) return null;
  return { o: s.o, prop: s.prop, key: track[i], next: track[i + 1] || null };
}

/** Every selected keyframe that can actually take a curve — the ones this
 *  panel writes to. A key with nothing after it has no segment to shape. */
function curveTargets() {
  const out = [];
  for (const s of sel) {
    const track = s.o.anim?.[s.prop];
    if (!track) continue;
    const i = track.findIndex(k => Math.abs(k.t - s.t) < 1e-6);
    if (i >= 0 && track[i + 1]) out.push({ o: s.o, prop: s.prop, key: track[i] });
  }
  return out;
}

/** Write one ease onto every target. `commit` pushes history; a live drag
 *  does not, matching what startKeyDrag does for the same reason. */
function setEase(ease, commit) {
  for (const t of curveTargets())
    t.o.anim[t.prop] = setKey(t.o.anim[t.prop], t.key.t, t.key.v, ease);
  applyAnimation();
  if (commit) pushHistory();
}

const plotX = x => x * PLOT;
const plotY = y => PLOT - y * PLOT;      // SVG y grows downward; a curve does not

function curvePath(h) {
  const [x1, y1, x2, y2] = h;
  return `M0 ${plotY(0)} C ${plotX(x1)} ${plotY(y1)}, ${plotX(x2)} ${plotY(y2)}, ${plotX(1)} ${plotY(1)}`;
}

export function renderCurvePanel() {
  const wrap = $('#tlCurve');
  if (!wrap) return;
  wrap.classList.toggle('hidden', !curveOpen);
  const btn = $('#tlCurveBtn');
  if (btn) { btn.classList.toggle('on', curveOpen); btn.setAttribute('aria-pressed', String(curveOpen)); }
  if (!curveOpen) return;
  wrap.innerHTML = '';

  const head = el('div', 'tlcurve__head');
  head.appendChild(el('span', 'tlcurve__title', 'Easing'));
  wrap.appendChild(head);

  const subject = curveSubject();
  if (!subject) {
    wrap.appendChild(el('p', 'tlcurve__note',
      'Select a keyframe to shape the segment that leaves it. Its easing is what happens between it and the next one.'));
    return;
  }
  const { key, next } = subject;
  if (!next) {
    wrap.appendChild(el('p', 'tlcurve__note',
      'This is the last keyframe on its track, so nothing leaves it. Easing describes a segment, and there is no segment after the end.'));
    return;
  }
  const many = curveTargets().length;
  head.appendChild(el('span', 'tlcurve__len',
    Math.max(1, toFrame(next.t - key.t)) + (toFrame(next.t - key.t) === 1 ? ' frame' : ' frames')));

  /* 'hold' and 'auto' have no honest pair of control points: hold is the
     absence of interpolation, and auto is a spline through the NEIGHBOURS
     rather than a reshaping of this segment's time. Say so rather than
     drawing a curve that is not the one being used. */
  const named = !isCurve(key.ease);
  const shapeless = key.ease === 'hold' || key.ease === 'auto';
  const handles = isCurve(key.ease) ? key.ease.slice()
                : (CURVE_PRESETS[key.ease] || CURVE_PRESETS.inout).slice();

  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('class', 'tlcurve__plot');
  /* THE BOX GROWS TO WHAT IT HAS TO SHOW. Reserving the full overshoot range
     always would draw an ordinary ease at a third of the height for the sake
     of room nothing is using; fitting it to the handles keeps the common case
     square — both axes are 0..1, and a stretched plot makes a symmetric curve
     look lopsided, which is the one thing it must not do. */
  const lo = Math.min(0, handles[1], handles[3]);
  const hi = Math.max(1, handles[1], handles[3]);
  const pad = 8;
  /* Through plotY, not from the curve values directly: SVG's y grows downward
     so the HIGHEST curve value is the SMALLEST y, and reading the box off the
     values without that flip puts it a hundred units from the thing it is
     supposed to contain. */
  const vbY = plotY(hi) - pad, vbH = (hi - lo) * PLOT + pad * 2;
  svg.setAttribute('viewBox', `${-pad} ${vbY} ${PLOT + pad * 2} ${vbH}`);
  /* And the element takes the same proportions, so the drawing fills it
     rather than being letterboxed inside a square that no longer fits. */
  svg.style.aspectRatio = `${PLOT + pad * 2} / ${vbH}`;
  const ns = (tag, attrs) => {
    const n = document.createElementNS('http://www.w3.org/2000/svg', tag);
    for (const k in attrs) n.setAttribute(k, attrs[k]);
    return n;
  };
  svg.appendChild(ns('rect', { class: 'tlcurve__box', x: 0, y: 0, width: PLOT, height: PLOT }));
  let readout = null;                     // built below; redraw keeps it in step
  const arms = [], grips = [];
  let path = null;
  if (!shapeless) {
    arms.push(svg.appendChild(ns('line', { class: 'tlcurve__arm', x1: 0, y1: plotY(0), x2: plotX(handles[0]), y2: plotY(handles[1]) })));
    arms.push(svg.appendChild(ns('line', { class: 'tlcurve__arm', x1: PLOT, y1: plotY(1), x2: plotX(handles[2]), y2: plotY(handles[3]) })));
    path = svg.appendChild(ns('path', { class: 'tlcurve__path', d: curvePath(handles) }));
  }
  svg.appendChild(ns('circle', { class: 'tlcurve__anchor', cx: 0, cy: plotY(0), r: 3 }));
  svg.appendChild(ns('circle', { class: 'tlcurve__anchor', cx: PLOT, cy: plotY(1), r: 3 }));

  /* Redraw in place, WITHOUT rebuilding the panel. Re-rendering on every
     pointermove would replace the very circle the pointer is holding — the
     same trap startKeyDrag documents for the lanes, and it fails the same
     way: the drag simply stops after one frame. */
  const redraw = () => {
    grips.forEach((c, g) => {
      c.setAttribute('cx', plotX(handles[g * 2]));
      c.setAttribute('cy', plotY(handles[g * 2 + 1]));
    });
    if (arms[0]) { arms[0].setAttribute('x2', plotX(handles[0])); arms[0].setAttribute('y2', plotY(handles[1])); }
    if (arms[1]) { arms[1].setAttribute('x2', plotX(handles[2])); arms[1].setAttribute('y2', plotY(handles[3])); }
    if (path) path.setAttribute('d', curvePath(handles));
    if (readout) readout.value = `cubic-bezier(${handles.map(n => +n.toFixed(2)).join(', ')})`;
  };

  if (!shapeless) for (const g of [0, 1]) {
    const c = ns('circle', { class: 'tlcurve__grip', r: 6,
      cx: plotX(handles[g * 2]), cy: plotY(handles[g * 2 + 1]) });
    c.addEventListener('pointerdown', ev => {
      ev.preventDefault();
      c.setPointerCapture(ev.pointerId);
      c.classList.add('tlcurve__grip--on');
      const move = e => {
        const r = svg.getBoundingClientRect();
        /* Straight from client pixels into curve space through the viewBox,
           so the mapping cannot drift from what is drawn. */
        const vb = svg.viewBox.baseVal;
        const ux = vb.x + ((e.clientX - r.left) / r.width) * vb.width;
        const uy = vb.y + ((e.clientY - r.top) / r.height) * vb.height;
        handles[g * 2] = clamp(ux / PLOT, 0, 1);
        handles[g * 2 + 1] = clamp((PLOT - uy) / PLOT, -CURVE_Y, 1 + CURVE_Y);
        setEase(handles.slice(), false);
        redraw();
        requestRender();
      };
      const up = e => {
        c.releasePointerCapture?.(e.pointerId);
        c.removeEventListener('pointermove', move);
        c.removeEventListener('pointerup', up);
        c.classList.remove('tlcurve__grip--on');
        setEase(handles.slice(), true);
        /* Now it is safe to rebuild: the silhouette in the lane has to become
           the custom-curve one, and the plot may need to grow for an
           overshoot the drag has just created. */
        renderTimeline();
      };
      c.addEventListener('pointermove', move);
      c.addEventListener('pointerup', up);
    });
    grips.push(c);
    svg.appendChild(c);
  }
  wrap.appendChild(svg);

  if (many > 1) wrap.appendChild(el('p', 'tlcurve__note', `Editing ${many} keyframes.`));
  if (shapeless)
    wrap.appendChild(el('p', 'tlcurve__note', key.ease === 'hold'
      ? 'Hold has no curve: the value does not travel, it waits and then jumps.'
      : 'Auto bezier is shaped by the keyframes either side, not by this segment alone, so it has no handles of its own. Pick another to start dragging.'));

  /* THE NINE NAMES, as chips. They set the NAME back rather than the
     equivalent handles, so nothing that exists today becomes unreachable and
     a key can always return to a shape the document can say in one word. */
  const chips = el('div', 'tlcurve__presets');
  for (const e of EASES) {
    const b = el('button', 'tlcurve__chip' + (named && key.ease === e ? ' on' : ''), e);
    b.onclick = () => { setEase(e, true); renderTimeline(); };
    chips.appendChild(b);
  }
  wrap.appendChild(chips);

  /* The numbers, readable and editable. cubic-bezier(...) is what designers
     paste to each other, and it is also how precision is reached here without
     inventing a nudge gesture for a 6px dot. */
  readout = el('input', 'tlcurve__num');
  const num = readout;
  num.type = 'text';
  num.spellcheck = false;
  num.value = shapeless ? key.ease
    : `cubic-bezier(${handles.map(n => +n.toFixed(2)).join(', ')})`;
  num.title = 'Four numbers: the two control points. Paste one in to apply it.';
  num.onchange = () => {
    const nums = (num.value.match(/-?\d*\.?\d+/g) || []).map(Number);
    if (nums.length === 4 && nums.every(Number.isFinite)) {
      setEase([clamp(nums[0], 0, 1), nums[1], clamp(nums[2], 0, 1), nums[3]], true);
      renderTimeline();
    } else renderCurvePanel();       // unreadable: put the real value back
  };
  wrap.appendChild(num);
}

function openKeyMenu(anchor, group, ease) {
  document.querySelector('.tlmenu')?.remove();
  const m = el('div', 'tlmenu');
  const NAMES = { linear: 'Linear', in: 'Ease in', out: 'Ease out',
                  inout: 'Ease in and out', auto: 'Auto bezier', hold: 'Hold',
                  back: 'Overshoot', bounce: 'Bounce', elastic: 'Elastic' };
  /* Acts on the whole selection when the clicked keyframe is part of it —
     otherwise setting five keyframes to Hold would mean opening five menus. */
  const targets = sel.length > 1 && group.some(isSelected) ? [...sel] : group;

  if (targets.length > 1) m.appendChild(el('p', 'tlmenu__note', `${targets.length} keyframes selected`));
  for (const e of EASES) {
    const b = el('button', 'tlmenu__item' + (ease === e ? ' on' : ''));
    b.innerHTML = `<svg class="tlmenu__ico" viewBox="0 0 ${D} ${D}">${keyShape(e)}</svg><span>${NAMES[e]}</span>`;
    b.onclick = () => {
      m.remove();
      for (const k of targets) {
        const key = (k.o.anim[k.prop] || []).find(x => Math.abs(x.t - k.t) < 1e-6);
        if (key) k.o.anim[k.prop] = setKey(k.o.anim[k.prop], k.t, key.v, e);
      }
      applyAnimation();
      pushHistory();
      renderTimeline();
    };
    m.appendChild(b);
  }

  m.appendChild(el('div', 'tlmenu__rule'));
  const dup = el('button', 'tlmenu__item',
    targets.length > 1 ? `Duplicate ${targets.length} keyframes at the playhead`
                       : 'Duplicate at the playhead');
  dup.onclick = () => {
    m.remove();
    copyKeys(targets);
    const n = pasteKeys();
    if (n) toast(`${n} keyframe${n === 1 ? '' : 's'} duplicated at the playhead.`);
  };
  m.appendChild(dup);

  const del = el('button', 'tlmenu__item tlmenu__item--dgr',
    targets.length > 1 ? `Delete ${targets.length} keyframes` : 'Delete keyframe');
  del.onclick = () => { m.remove(); deleteKeys(targets); };
  m.appendChild(del);
  /* Removing the last keyframe of a track removes the track — say so, rather
     than leaving someone to discover that deleting one diamond took the whole
     row with it. */
  if (targets.length === 1 && targets[0].o.anim[targets[0].prop].length === 1)
    m.appendChild(el('p', 'tlmenu__note',
      'The last one — this stops animating ' + ANIMATABLE[targets[0].prop].label.toLowerCase() + '.'));

  const r = anchor.getBoundingClientRect();
  m.style.left = Math.min(r.left, innerWidth - 200) + 'px';
  m.style.top = (r.top - 8) + 'px';
  m.style.transform = 'translateY(-100%)';
  document.body.appendChild(m);
  /* Captured, and on the NEXT tick — the click that opened this would
     otherwise close it immediately. */
  setTimeout(() => addEventListener('pointerdown', function close(ev) {
    if (m.contains(ev.target)) return;
    m.remove();
    removeEventListener('pointerdown', close, true);
  }, true), 0);
}

/** Remove keyframes, and any track left empty by it. */
export function deleteKeys(list) {
  for (const { o, prop, t } of [...list]) {
    if (!o.anim || !o.anim[prop]) continue;
    const left = removeKey(o.anim[prop], t);
    if (left) o.anim[prop] = left; else removeTrack(o, prop);
  }
  sel = [];
  applyAnimation();
  pushHistory();
  renderTimeline();
  renderRight();
}

/* ── copy and paste ─────────────────────────────────────────────────────────
   Times are stored RELATIVE to the earliest keyframe copied, so a paste lands
   the group at the playhead with its internal spacing intact — which is the
   whole reason to copy a group rather than one key. Values are copied out,
   not referenced: the originals can be edited or deleted afterwards and the
   clipboard still holds what was on screen when you pressed the key.

   Keyframes go back onto the object they came from. Pasting across objects
   would have to decide what a Position track means on a light, or a colour
   on a camera, and "duplicate this move later in the piece" is the thing
   people actually reach for. */
let clipboard = null;

export function copyKeys(list) {
  const src = [...list].filter(k => k.o.anim && k.o.anim[k.prop]);
  if (!src.length) return 0;
  const base = Math.min(...src.map(k => k.t));
  clipboard = src.map(({ o, prop, t }) => {
    const key = o.anim[prop].find(x => Math.abs(x.t - t) < 1e-6);
    return key ? { objId: o.id, prop, dt: t - base, v: copyValue(key.v), ease: key.ease } : null;
  }).filter(Boolean);
  return clipboard.length;
}

/** Paste at the playhead. Returns how many landed. */
export function pasteKeys() {
  if (!clipboard || !clipboard.length) return 0;
  const at = S.playhead;
  const landed = [];
  for (const c of clipboard) {
    const o = S.objects.find(x => x.id === c.objId);
    /* The object may have been deleted since the copy. Skipping is the only
       sensible reading — there is nowhere to put it — and the rest of the
       group should still paste rather than the whole action failing. */
    if (!o) continue;
    const t = quantise(clamp(at + c.dt, 0, S.timeline.duration));
    if (!o.anim) o.anim = {};
    o.anim[c.prop] = setKey(o.anim[c.prop], t, c.v, c.ease);
    landed.push({ o, prop: c.prop, t });
  }
  if (!landed.length) return 0;
  /* Select what was just pasted, not what was copied: the new keys are what
     you are about to nudge, and leaving the originals selected would make the
     next drag move the wrong ones. */
  sel = landed;
  applyAnimation();
  pushHistory();
  renderTimeline();
  renderRight();
  return landed.length;
}

/* Double-clicking empty lane space puts a keyframe there, holding whatever
   the value is at that instant — how you add a hold, and how you split an
   existing move in two. */
function addLaneKeying(lane, o, prop) {
  lane.addEventListener('dblclick', ev => {
    if (ev.target.closest('.tlkey')) return;
    const t = quantise(xToT(ev.clientX - lane.getBoundingClientRect().left));
    setPlayhead(t);
    o.anim[prop] = setKey(o.anim[prop], t, readProp(o, prop));
    applyAnimation();
    pushHistory();
    renderTimeline();
  });
}

/* Copy the value at 0s to a keyframe at the very end, so the last frame
   matches the first.

   Worth a dedicated button rather than leaving it to be done by hand. Every
   screen this app targets plays its content on a LOOP, so the seam where the
   last frame meets the first is a real part of the deliverable — and it is
   the one part you cannot see while authoring, because the playhead stops at
   the end rather than wrapping round to the beginning in front of you. A
   piece that visibly jumps every few seconds on a shop window is the most
   likely way this feature goes wrong in the field. */
function loopTrack(o, prop) {
  const keys = o.anim?.[prop];
  if (!keys || !keys.length) return;
  const first = keys[0];
  /* The value AT ZERO, which is not always the first keyframe's — a track
     whose first key sits at 0.5s holds that value backwards to the start
     (evalTrack clamps rather than extrapolating), and the frame at 0 is what
     the loop actually returns to. */
  const v = evalTrack(keys, 0);
  o.anim[prop] = setKey(keys, S.timeline.duration, v, first.ease);
  applyAnimation();
  pushHistory();
  renderTimeline();
}

const LOOP_ICON = '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><path d="M4.2 5.4h6.4a2.6 2.6 0 0 1 0 5.2H5.4"/><path d="M6.2 3.4 4.1 5.4l2.1 2"/></svg>';

function loopButton(title, onClick) {
  const b = el('button', 'tlprop__loop');
  b.innerHTML = LOOP_ICON;
  b.title = title;
  b.onclick = ev => { ev.stopPropagation(); onClick(); };
  return b;
}

function propRow(o, prop, names, lanes, on) {
  const name = el('div', 'tlprop' + (on ? ' on' : ''));
  name.appendChild(el('span', 'tlprop__label', ANIMATABLE[prop].label));
  name.appendChild(loopButton(
    'Make ' + ANIMATABLE[prop].label.toLowerCase() + ' loop seamlessly — copies the value at 0s to the end, ' +
    'so the last frame matches the first',
    () => loopTrack(o, prop)));
  const del = el('button', 'tlprop__x');
  del.innerHTML = '&times;';
  del.title = 'Stop animating ' + ANIMATABLE[prop].label.toLowerCase() +
              ' — the value stays where the playhead has it';
  del.onclick = () => { removeTrack(o, prop); pushHistory(); renderTimeline(); renderRight(); };
  name.appendChild(del);
  names.appendChild(name);

  const lane = el('div', 'tllane' + (on ? ' on' : ''));
  /* Clustered for the same reason the folded object row is: two keys on
     neighbouring frames are two pixels apart at zoom 1, and drawing two
     diamonds there says "two things you can tell apart" when there are not. */
  for (const g of clusterKeys(o.anim[prop].map(k => ({ o, prop, t: k.t, ease: k.ease }))))
    lane.appendChild(keyframeEl(g.items, g.eases.size === 1 ? g.ease : 'linear', g.span));
  addLaneKeying(lane, o, prop);
  lanes.appendChild(lane);
}

/* ── one diamond per thing you can actually see ───────────────────────────

   Keyframes used to be grouped into a single diamond only when they sat at
   the SAME instant. That is a rule about the data, and the timeline is a
   picture: zoomed out far enough, a frame is a couple of pixels, so keys
   three frames apart drew three diamonds inside one diamond's width. The
   result was a smear nobody could read, count, or click a single member of —
   and the multi marker lied, because each of those overlapping diamonds
   still believed it stood for one key.

   So the grouping is in PIXELS, at the zoom being drawn at. Closer together
   than a diamond is wide and they are, by definition, overlapping — there is
   no version of the picture where they are separate things, so they are
   drawn as the one thing they look like. Zooming in pulls them apart again,
   which is both the fix and the way to reach a single key: the tooltip says
   so when a diamond is standing for several.

   Not a data merge. Nothing is moved, nothing is deleted, and the keyframes
   are exactly where they were — this is only how many marks get drawn. */
const MERGE_PX = D;                       // a diamond's own width: closer than this and they overlap

/**
 * Group keys whose diamonds would overlap at the current zoom.
 *
 * Greedy from each cluster's START rather than from the previous key, so a
 * run of evenly-spaced keys cannot chain into one enormous group — every
 * cluster is at most MERGE_PX wide, which is the claim being made about it.
 *
 * @param {Array<{o:object, prop:string, t:number, ease:string}>} entries
 * @returns {Array<{t:number, items:Array, eases:Set, span:number}>}
 */
function clusterKeys(entries) {
  const win = (MERGE_PX / laneWidth()) * S.timeline.duration;
  const sorted = entries.slice().sort((a, b) => a.t - b.t);
  const out = [];
  for (const e of sorted) {
    const g = out[out.length - 1];
    /* Counted as TEXT, because two equal curves are two different arrays and
       a Set of them would call one cluster "mixed" — drawing the fallback
       silhouette over keys that all agree. `ease` carries the real value for
       whichever cluster turns out to be unanimous. */
    const id = isCurve(e.ease) ? e.ease.join(',') : e.ease;
    if (g && e.t - g.t < win) {
      g.items.push({ o: e.o, prop: e.prop, t: e.t });
      g.eases.add(id);
      g.span = e.t - g.t;
    } else {
      out.push({ t: e.t, items: [{ o: e.o, prop: e.prop, t: e.t }],
                 eases: new Set([id]), ease: e.ease, span: 0 });
    }
  }
  return out;
}

/** Every keyframe of an object, clustered — what the object's own row shows
 *  while its properties are folded away. */
function mergedKeys(o) {
  const all = [];
  for (const [prop, keys] of Object.entries(o.anim || {}))
    for (const k of keys) all.push({ o, prop, t: k.t, ease: k.ease });
  return clusterKeys(all);
}

/** Whether any keyframes are selected — asked by ui/interaction.js before it
 *  acts on Delete, so the two never both claim the key. A function rather
 *  than the array itself: the selection is this module's business, and the
 *  only thing anyone outside needs is whether it is empty. */
export function hasKeySelection() { return sel.length > 0; }

export function renderTimeline() {
  const names = $('#tlNames'), tracks = $('#tlTracks');
  if (!names || !tracks) return;
  names.innerHTML = ''; tracks.innerHTML = '';
  lanesEl = $('#tlLanes');

  /* THE SCENE LIST'S ORDER, which is S.objects reversed — newest at the top,
     the way every layer list works. The dock used to walk the array as stored,
     so the two read as mirror images of each other and the row you had just
     clicked in one was at the opposite end of the other. */
  const animated = S.objects.slice().reverse().filter(o => o.anim && Object.keys(o.anim).length);
  $('#tlEmpty').classList.toggle('hidden', animated.length > 0);
  /* The lanes are NOT hidden when nothing is animated. The ruler is the only
     way to move the playhead, and the playhead is where a stopwatch drops
     its first keyframe — so hiding the ruler until a keyframe existed meant
     the first one could only ever land wherever the playhead happened to
     already be. An empty timeline is still a timeline you scrub. */
  $('#tlBody').classList.toggle('tlbody--empty', animated.length === 0);

  /* A selected keyframe that has since been deleted — or whose whole track
     has — must not linger in the selection and resurrect on the next drag. */
  sel = sel.filter(s => s.o.anim && s.o.anim[s.prop] && hasKeyAt(s.o.anim[s.prop], s.t));

  for (const o of animated) {
    const shut = collapsed.has(o.id);
    const trackCount = Object.keys(o.anim).length;
    /* Carried through to the lanes as well as the name, so selecting an object
       anywhere — the Scene list, the viewport — marks the whole row group it
       owns. With a dozen animated objects stacked up, finding the tracks that
       belong to the thing you just clicked was otherwise a matter of counting
       rows across the gap between the two columns. */
    const on = o.id === S.selId;

    const head = el('div', 'tlobj' + (on ? ' on' : ''));
    const tri = el('button', 'tlobj__tri' + (shut ? ' shut' : ''));
    tri.innerHTML = '<svg viewBox="0 0 10 10"><path d="M2.6 1.4 7.4 5 2.6 8.6z"/></svg>';
    tri.title = shut ? 'Show this object’s properties' : 'Fold this object’s properties away';
    tri.onclick = ev => {
      ev.stopPropagation();
      if (shut) collapsed.delete(o.id); else collapsed.add(o.id);
      renderTimeline();
    };
    head.appendChild(tri);
    const sw = el('span', 'sw'); sw.style.background = o.color;
    head.appendChild(sw);
    head.appendChild(el('span', 'nm', o.name));
    if (shut) head.appendChild(el('span', 'tlobj__count', trackCount + (trackCount === 1 ? ' track' : ' tracks')));
    head.appendChild(loopButton(
      'Make every track on ' + o.name + ' loop seamlessly',
      () => { for (const prop in ANIMATABLE) if (o.anim[prop]) loopTrack(o, prop); }));
    head.onclick = () => select(o.id);
    names.appendChild(head);

    /* The object's own lane carries the merged keyframes while it is folded,
       and is an empty header strip while it is not. The same element either
       way, so the two columns stay in lockstep without a spacer. */
    const objLane = el('div', 'tllane tllane--obj' + (shut ? ' tllane--merged' : '') + (on ? ' on' : ''));
    if (shut)
      for (const g of mergedKeys(o))
        objLane.appendChild(keyframeEl(g.items, g.eases.size === 1 ? g.ease : 'linear', g.span));
    tracks.appendChild(objLane);

    if (!shut) for (const prop in ANIMATABLE) if (o.anim[prop]) propRow(o, prop, names, tracks, on);
  }

  /* .tlscroll's own width has to be set explicitly — its ruler and track
     children only hold absolutely-positioned ticks and diamonds, and a
     browser does not grow a box to fit absolutely-positioned content, so
     without this #tlLanes never has anything to actually scroll past 100%. */
  const scroll = $('#tlScroll');
  if (scroll) scroll.style.width = (laneWidth() + LANE_PAD * 2) + 'px';

  buildRuler($('#tlRuler'));
  syncPlayhead();

  const pct = $('#tlZoomPct');
  if (pct) pct.textContent = Math.round(zoom * 100) + '%';

  /* The duration and fps fields are inputs, so it is tempting to treat them as
     the source of truth and set them once. They are not: undo restores a
     different duration, opening a project brings its own, and the export cap
     clamps what was typed. Left unsynced, the field sat at 5 while the
     timeline ran for 3 — and the field is the number people trust.
     Skipped while focused, so it cannot overwrite something mid-type. */
  const dur = $('#tlDur'), fps = $('#tlFps');
  if (dur && document.activeElement !== dur) dur.value = S.timeline.duration;
  if (fps && document.activeElement !== fps) fps.value = String(S.timeline.fps);

  /* Last: the lanes have just been rebuilt, so whatever the panel is showing
     was read off keyframes that may no longer be the ones on screen. */
  renderCurvePanel();
}

/** The hot path — a couple of style writes and a text node, no structure. */
export function syncPlayhead() {
  const head = $('#tlHead'), label = $('#tlTime');
  if (!head) return;
  head.style.left = tToX(S.playhead) + 'px';
  label.textContent = `${fmtTime(S.playhead)} / ${fmtTime(S.timeline.duration)}`;
}

/* ── the dock ────────────────────────────────────────────────────────────── */

function setOpen(open) {
  $('#timeline').classList.toggle('tldock--closed', !open);
  $('#tlCollapse').textContent = open ? 'Hide' : 'Show';
  $('#tlCollapse').title = open ? 'Collapse the timeline' : 'Show the timeline';
  try { localStorage.setItem(COLLAPSE_KEY, open ? '1' : '0'); } catch (_) {}
  /* The viewport is a grid row above this one, so opening or closing the dock
     resizes the canvas. The ResizeObserver in main.js catches it, but only
     after layout — nudging a render here keeps the two in step on the same
     frame instead of one frame late, which is visible as a flash. */
  requestRender();
}

/** Show the dock in Design and Motion, hide it in Calibrate — same rule the
 *  rails and the view tabs follow (see ui/chrome.js's goStage). */
export function setTimelineStage(stage) {
  const d = $('#timeline');
  if (d) d.classList.toggle('hidden', stage === 'calib');
  syncTimelineLock();
}

/* The dock, without a Motion licence.

   Shown rather than hidden — the point of a visible lock is that someone can
   find out the feature exists — but collapsed to its own header bar rather
   than left at full height. A permanently inert 200px strip across the bottom
   of every project is a worse answer than an honest one-line one: it costs
   the room the feature would have earned and gives nothing back.

   .tldock--locked drives that in CSS, and the explainer goes where the dock
   already puts its "nothing here yet" copy (#tlEmpty), so there is one place
   the dock says why it is empty rather than two competing ones. */
/* The dock's own empty-state copy, as it ships in index.html. Captured once
   rather than restored from a literal here, so the two can never disagree —
   the locked message overwrites this element, and it has to be put back
   verbatim when a licence lands. */
let tlEmptyCopy = null;

export function syncTimelineLock() {
  const d = $('#timeline');
  if (!d) return;
  const gate = canUseMotion();
  d.classList.toggle('tldock--locked', !gate.ok);
  const empty = $('#tlEmpty');
  if (!empty) return;
  if (tlEmptyCopy === null) tlEmptyCopy = empty.textContent;

  if (gate.ok) { empty.textContent = tlEmptyCopy; return; }

  empty.textContent = gate.reason + ' ';
  /* Built rather than written into the markup: textContent above wipes the
     element every time, and the link must not survive into the unlocked
     copy restored on the line before. */
  /* A button now, not a link: it opens the request form in place rather than
     sending somebody to another domain mid-project. Same class, so it keeps
     the styling and the two checks in verify.mjs that watch for it. */
  const a = el('button', 'tlempty__link', MOTION_CTA);
  a.type = 'button';
  a.onclick = showTrial;
  empty.appendChild(a);
}
on('motion:changed', syncTimelineLock);
on('auth:changed', syncTimelineLock);

/* ── resizing the dock ───────────────────────────────────────────────────── */

/* .tlbody normally sizes itself from CSS (max-height:min(34vh,300px), more in
   Motion) and scrolls past that. A drag overrides both height and max-height
   inline, which is the only way to beat that cap — max-height still clamps an
   explicit height otherwise. null clears the override and returns to the CSS
   default, which is what a double-click asks for. */
function applyBodyHeight(px) {
  const body = $('#tlBody');
  if (!body) return;
  if (px == null) { body.style.height = ''; body.style.maxHeight = ''; return; }
  const h = clamp(Math.round(px), MIN_BODY_H, Math.round(innerHeight * .8));
  body.style.height = h + 'px';
  body.style.maxHeight = h + 'px';
}

function initTimelineResize() {
  const handle = $('#tlResize');
  if (!handle) return;

  handle.addEventListener('pointerdown', ev => {
    if (ev.button !== 0) return;
    ev.preventDefault();
    const startY = ev.clientY, startH = $('#tlBody').getBoundingClientRect().height;
    handle.setPointerCapture(ev.pointerId);
    /* The handle sits at the TOP of the dock, which hangs off the bottom of
       the window — dragging it up (clientY decreasing) has to grow the dock,
       the opposite sign from a handle at the bottom of a box would use. */
    const move = e => applyBodyHeight(startH + (startY - e.clientY));
    const up = () => {
      handle.releasePointerCapture(ev.pointerId);
      removeEventListener('pointermove', move);
      removeEventListener('pointerup', up);
      try {
        const h = $('#tlBody').style.height;
        if (h) localStorage.setItem(RESIZE_KEY, h); else localStorage.removeItem(RESIZE_KEY);
      } catch (_) {}
    };
    addEventListener('pointermove', move);
    addEventListener('pointerup', up);
  });
  /* Double-click to give up on a manual size and go back to letting the
     stage (Design vs Motion) decide, the same escape hatch a dragged column
     width or panel split usually offers. */
  handle.addEventListener('dblclick', () => {
    applyBodyHeight(null);
    try { localStorage.removeItem(RESIZE_KEY); } catch (_) {}
  });

  let saved = null;
  try { saved = localStorage.getItem(RESIZE_KEY); } catch (_) {}
  if (saved) applyBodyHeight(parseFloat(saved));
}

/* Rubber-band selection over the lanes. Dragging in the tracks area used to
   scrub — which the ruler already does, and does better because it is where
   the times are written. A marquee needs a large empty surface, and the
   tracks area is exactly that. */
function startMarquee(ev) {
  const host = $('#tlTracks');
  const box = host.getBoundingClientRect();
  const from = { x: ev.clientX, y: ev.clientY };
  const rect = el('div', 'tlmarquee');
  host.appendChild(rect);
  const before = ev.shiftKey ? [...sel] : [];

  const move = e => {
    const x0 = Math.min(from.x, e.clientX), x1 = Math.max(from.x, e.clientX);
    const y0 = Math.min(from.y, e.clientY), y1 = Math.max(from.y, e.clientY);
    rect.style.left = (x0 - box.left) + 'px';
    rect.style.top = (y0 - box.top + host.scrollTop) + 'px';
    rect.style.width = (x1 - x0) + 'px';
    rect.style.height = (y1 - y0) + 'px';

    sel = [...before];
    for (const n of document.querySelectorAll('.tlkey')) {
      const r = n.getBoundingClientRect();
      if (r.right >= x0 && r.left <= x1 && r.bottom >= y0 && r.top <= y1) addToSelection(n.__keys || []);
    }
    paintSelection();
  };
  const up = () => {
    removeEventListener('pointermove', move);
    removeEventListener('pointerup', up);
    rect.remove();
  };
  addEventListener('pointermove', move);
  addEventListener('pointerup', up);
}

export function initTimeline() {
  const dock = $('#timeline');
  if (!dock) return;

  $('#tlPlay').onclick = togglePlay;
  /* Not pushed to history: whether the preview repeats is a way of watching,
     not an edit to the piece — undo should never surprise someone by
     switching it back mid-session. It still saves with the project, because
     it describes how this piece is meant to be viewed. */
  $('#tlLoop').onclick = () => {
    S.timeline.loop = S.timeline.loop === false;
    paintLoop();
  };
  $('#tlCollapse').onclick = () => setOpen(dock.classList.contains('tldock--closed'));

  const dur = $('#tlDur'), fps = $('#tlFps');
  /* Values are not set here — renderTimeline() at the end of this function
     does it, and does it again on every rebuild, which is what keeps them
     honest after an undo or a project load. */
  /* The cap is not arbitrary and it is not a licence tier, so say which it
     is. Thirty seconds at 60fps and 4K is already eighteen hundred
     supersampled renders through a browser tab — the limit is what the
     machine can finish, not what you are allowed. */
  dur.title = 'How long the piece runs, in seconds. Up to 30 — beyond that a browser ' +
              'tab takes long enough to render the frames that it is not worth waiting through.';
  fps.title = 'Frames per second. 30 is the safe default for signage players; ' +
              '60 doubles the render time for a difference few passers-by will see.';
  dur.addEventListener('change', () => {
    /* Capped at the same 30 seconds the exporter will accept, and refused
       below a tenth of a second — the lane maths divides by this. */
    S.timeline.duration = clamp(+dur.value || 5, .1, 30);
    dur.value = S.timeline.duration;
    if (S.playhead > S.timeline.duration) setPlayhead(S.timeline.duration);
    pushHistory();
    renderTimeline();
  });
  fps.addEventListener('change', () => {
    S.timeline.fps = +fps.value;
    snapKeysToFrames();       // a key on a 24fps frame is between two 30fps ones
    /* The playhead sat on a frame boundary of the OLD rate and is now between
       two frames of the new one. */
    setPlayhead(quantise(S.playhead));
    pushHistory();
    renderTimeline();
  });

  /* Scrubbing lives on the ruler; the tracks area rubber-bands. Playback
     stops on the first press — a playhead being dragged and also running is
     nobody's intent. */
  /* Measured from #tlScroll, the content that moves when the lanes are scrolled,
     not #tlLanes, the window onto it. Zoomed in and scrolled along, the window's
     edge is not where time 0 is, and the playhead landed that far from the
     cursor — the whole scroll offset to the left of where you clicked. */
  const scrubFrom = ev => setPlayhead(xToT(ev.clientX - $('#tlScroll').getBoundingClientRect().left));
  $('#tlRuler').addEventListener('pointerdown', ev => {
    if (ev.button !== 0) return;
    pause();
    scrubFrom(ev);
    const move = e => scrubFrom(e);
    const up = () => { removeEventListener('pointermove', move); removeEventListener('pointerup', up); };
    addEventListener('pointermove', move);
    addEventListener('pointerup', up);
  });
  $('#tlTracks').addEventListener('pointerdown', ev => {
    if (ev.button !== 0 || ev.target.closest('.tlkey')) return;
    if (!ev.shiftKey) { sel = []; paintSelection(); }
    startMarquee(ev);
  });

  /* Wheel zooms, anchored on the time under the cursor so the thing you are
     looking at is the thing that grows, not the left edge of the timeline. A
     horizontal wheel — Shift+wheel on most mice, a trackpad's own two-finger
     swipe — is left alone to pan natively instead; only a bigger deltaY than
     deltaX is treated as an intent to zoom. */
  $('#tlLanes').addEventListener('wheel', ev => {
    if (Math.abs(ev.deltaX) > Math.abs(ev.deltaY)) return;
    ev.preventDefault();
    const cursorPx = ev.clientX - lanesEl.getBoundingClientRect().left;
    const t = xToT(lanesEl.scrollLeft + cursorPx);
    setZoom(zoom * Math.pow(1.0015, -ev.deltaY), t, cursorPx);
  }, { passive: false });
  $('#tlCurveBtn').onclick = () => {
    curveOpen = !curveOpen;
    try { localStorage.setItem(CURVE_KEY, curveOpen ? '1' : '0'); } catch (_) { /* private mode */ }
    renderCurvePanel();
  };
  $('#tlZoomIn').onclick = () => setZoom(zoom * 1.5);
  $('#tlZoomOut').onclick = () => setZoom(zoom / 1.5);
  $('#tlZoomPct').onclick = () => setZoom(1);

  /* Delete removes the selected keyframes.
 
     It used to also require the dock to be hovered or focused, on the theory
     that this would keep it from fighting the viewport's Delete. It did not:
     ui/interaction.js registers its keydown at module load and this one runs
     inside initTimeline, so the viewport's handler went first and deleted the
     whole OBJECT — hover or no hover — which is precisely the mistake that
     guard was written to prevent. The viewport now stands down while
     keyframes are selected (see hasKeySelection), which makes the hover test
     unnecessary as well as ineffective: having selected keyframes IS the
     statement of intent, and needing to keep the mouse parked over the dock
     to act on them was its own small annoyance. */
  addEventListener('keydown', ev => {
    if (typingTarget(ev.target)) return;
    /* Delete inside a number field belongs to the field. Without this the
       viewport was fixed but this handler still fired, so correcting a size
       destroyed the selected KEYFRAMES instead of the object — the same
       mistake one level down. See state/store.js's fieldOwnsKey. */
    if (fieldOwnsKey(ev.target, ev.key)) return;
    const mod = ev.metaKey || ev.ctrlKey;
    const k = ev.key.toLowerCase();

    if (mod && k === 'c' && sel.length) {
      const n = copyKeys(sel);
      if (n) toast(`${n} keyframe${n === 1 ? '' : 's'} copied. Move the playhead and paste.`);
      ev.preventDefault();
      return;
    }
    if (mod && k === 'v' && clipboard && clipboard.length) {
      const n = pasteKeys();
      if (n) toast(`${n} keyframe${n === 1 ? '' : 's'} pasted at the playhead.`);
      ev.preventDefault();
      return;
    }
    /* Duplicate is copy-and-paste in one press, and it takes Ctrl+D from the
       viewport's duplicate-object for as long as keyframes are selected —
       the same rule Delete follows (see hasKeySelection). */
    if (mod && k === 'd' && sel.length) {
      copyKeys(sel);
      const n = pasteKeys();
      if (n) toast(`${n} keyframe${n === 1 ? '' : 's'} duplicated at the playhead.`);
      ev.preventDefault();
      return;
    }
    if (ev.key !== 'Delete' && ev.key !== 'Backspace') return;
    if (!sel.length) return;
    ev.preventDefault();
    deleteKeys(sel);
  });

  on('playhead:moved', syncPlayhead);
  /* Named, so the initial state is painted by the same code that paints every
     later one — the button used to boot as an empty square because nothing
     had emitted 'playing:changed' yet. */
  const paintLoop = () => {
    const on = S.timeline.loop !== false;
    const b = $('#tlLoop');
    b.classList.toggle('on', on);
    b.setAttribute('aria-pressed', String(on));
    b.title = on ? 'Looping — playback repeats. Click to play once and stop.'
                 : 'Playing once — stops on the last frame. Click to loop.';
  };
  paintLoop();
  /* Repainted on every rebuild for the same reason the duration and fps
     fields are: undo, a project load and the export cap can all change it
     from underneath, and a control showing the wrong state is worse than no
     control. */
  on('anim:changed', paintLoop);
  on('project:changed', paintLoop);

  /* The play triangle starts at x=4.4, not 5, and that 0.6 is deliberate.
     A right-pointing triangle centred on its BOUNDING BOX reads as sitting too
     far left, because its area is banked against the flat edge — so it wants a
     nudge right. It had one, but too much of one: the path ran 5 to 12.92 in a
     16-wide box, putting the bounding box a full pixel right of centre on a
     15px icon, which left visibly more air on the left than the right.
     Measured, not eyeballed — rendered at 16x and the ink compared against the
     button box. Now about a third of that, which is compensation rather than
     an offset you can see.

     The pause glyph needs none of this and gets none: two rects spanning 4 to
     12, symmetrical about the centre by construction. */
  const paintPlay = playing => {
    $('#tlPlay').classList.toggle('on', playing);
    $('#tlPlay').title = playing ? 'Pause (Space)' : 'Play (Space)';
    $('#tlPlay').innerHTML = playing
      ? '<svg viewBox="0 0 16 16" fill="currentColor"><rect x="4" y="3" width="3" height="10" rx="1"/><rect x="9" y="3" width="3" height="10" rx="1"/></svg>'
      : '<svg viewBox="0 0 16 16" fill="currentColor"><path d="M4.4 3.4v9.2a.6.6 0 0 0 .92.5l7-4.6a.6.6 0 0 0 0-1l-7-4.6A.6.6 0 0 0 4.4 3.4z"/></svg>';
  };
  on('playing:changed', paintPlay);
  paintPlay(S.playing);
  on('anim:changed', () => renderTimeline());
  on('scene:changed', () => renderTimeline());
  on('sel:changed', () => renderTimeline());

  /* The lane geometry is in pixels, so every keyframe is in the wrong place
     after a resize until the rows are rebuilt.

     Guarded on the width actually changing, because renderTimeline WRITES
     layout — it sets .tlscroll's pixel width — and .tlscroll lives inside the
     element being observed. Re-rendering unconditionally therefore fed the
     observer from inside its own callback: at some zoom levels the write
     added or removed the horizontal scrollbar, which changed clientWidth,
     which fired the observer again. The browser notices that and reports
     "ResizeObserver loop completed with undelivered notifications" — which is
     benign in itself, but was reaching the global error handler and taking
     the whole stage down with it. */
  let lastLaneW = 0;
  new ResizeObserver(() => {
    const w = $('#tlLanes')?.clientWidth || 0;
    if (w === lastLaneW) return;
    lastLaneW = w;
    renderTimeline();
  }).observe($('#tlLanes'));

  let open = true;
  try { open = localStorage.getItem(COLLAPSE_KEY) !== '0'; } catch (_) {}
  setOpen(open);
  initTimelineResize();
  renderTimeline();
}

/* ── the stopwatch ─────────────────────────────────────────────────────────
   Wraps a row built by ui/controls.js and drops a toggle into its label.
   A decorator rather than a parameter threaded through every row helper:
   fieldRow, numRow, vec3Row, sliderRow and colorRow all start their row with
   a <label>, so one function can decorate any of them and none of them has to
   learn what animation is. */

const WATCH = '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.4"><circle cx="8" cy="9" r="5"/><path d="M8 6.4V9l1.8 1.1M6.2 1.8h3.6"/></svg>';

/**
 * @param {HTMLElement} row  a row from ui/controls.js
 * @param {object} o         the object it edits
 * @param {string} prop      an ANIMATABLE key
 */
/* KEYFRAME X, Y AND Z SEPARATELY.
   The chip after a vector's label flips it between one track and three; while
   it is three, every axis cell gets its own dot, so an object can slide in X
   while it spins in Y and holds still in Z. Nothing moves when the form
   changes (anim.js splitVecTrack / mergeAxisTracks say exactly what is kept).
   Not animated yet: the chip starts it already separated, since that is the
   only reason to press it. */
const AXCHIP = '<svg viewBox="0 0 12 12"><path d="M1.5 2.5h9" stroke="var(--gz-x)"/><path d="M1.5 6h9" stroke="var(--gz-y)"/><path d="M1.5 9.5h9" stroke="var(--gz-z)"/></svg>';
function axisControls(host, label, o, prop, on, present) {
  const sep = present.length > 0;
  const chip = el('button', 'axchip' + (sep ? ' on' : ''));
  chip.innerHTML = AXCHIP;
  chip.type = 'button';
  chip.title = sep ? 'Combine X, Y and Z into one track'
    : on ? 'Keyframe X, Y and Z separately'
    : 'Animate X, Y and Z separately';
  chip.onclick = ev => {
    ev.preventDefault(); ev.stopPropagation();
    if (sep) combineProp(o, prop);
    else { if (!on) addTrack(o, prop); splitProp(o, prop); }
    pushHistory(); renderRight(); renderTimeline();
  };
  label.appendChild(chip);
  if (!sep) return;
  const cells = host.querySelectorAll('.mcell, .f3 > div');
  AXES.forEach((a, i) => {
    const cell = cells[i];
    if (!cell) return;
    const track = prop + '.' + a;
    const has = !!o.anim[track];
    const d = el('button', 'tlaxis' + (has ? ' on' : ''));
    d.type = 'button';
    d.dataset.track = track;
    d.title = has ? `Stop animating ${ANIMATABLE[track].label.toLowerCase()}`
      : `Animate ${ANIMATABLE[track].label.toLowerCase()} — adds a keyframe at the playhead`;
    d.onclick = ev => {
      ev.preventDefault(); ev.stopPropagation();
      if (has) removeTrack(o, track); else addTrack(o, track);
      pushHistory(); renderRight(); renderTimeline();
    };
    cell.appendChild(d);
  });
}

export function stopwatch(row, o, prop) {
  if (!ANIMATABLE[prop]) return row;
  /* Gated HERE, at the one place every stopwatch is built, rather than per
     caller. Five of the ten call sites are in the DESIGN inspector
     (ui/rail-right.js — the transform grid, light colour and intensity,
     object tint and opacity), so gating by stage would have left keyframes
     creatable from Design by anyone. Returning the row untouched is the
     whole gate: no button, nothing to explain, because an unlicensed user in
     Design is not being refused anything they asked for — the explaining is
     the Motion tab's job. */
  if (!canUseMotion().ok) return row;

  /* Most rows are a <div> whose first child is the <label>. checkRow is not:
     it returns the <label> ITSELF as the row (see ui/controls.js), so a plain
     querySelector('label') finds nothing and used to return the row
     untouched — which is how Visible ended up as the one animatable property
     with no way to animate it, silently. Wrapping is better than reaching
     inside: a <button> inside a <label> also activates that label, so the
     stopwatch would have toggled the checkbox on its way past. */
  let host = row;
  if (row.matches('label')) {
    host = el('div', 'f f--chk');
    host.appendChild(row);
  }
  const label = host.matches('label') ? host : host.querySelector('label');
  if (!label) return row;
  const target = host === row ? label : host;

  /* A vector is animated as one track or as three (X, Y, Z), never both, so
     "animated" for pos/rot/scale means either. */
  const axes = AXIS_TRACKS[prop] ? axisTracksPresent(o, prop) : [];
  const on = !!(o.anim && o.anim[prop]) || axes.length > 0;
  const b = el('button', 'tlwatch' + (on ? ' on' : ''));
  b.innerHTML = WATCH;
  b.type = 'button';
  b.title = on
    ? `Stop animating ${ANIMATABLE[prop].label.toLowerCase()}`
    : `Animate ${ANIMATABLE[prop].label.toLowerCase()} — adds a keyframe at the playhead`;
  b.onclick = ev => {
    ev.preventDefault();
    ev.stopPropagation();
    if (on) { removeTrack(o, prop); for (const p of axes) removeTrack(o, p); } else addTrack(o, prop);
    pushHistory();
    renderRight();
    renderTimeline();
  };
  target.insertBefore(b, target.firstChild);
  if (AXIS_TRACKS[prop]) axisControls(host, label, o, prop, on, axes);

  /* Used to also drop a second button here — a diamond, filled when the
     playhead sat on a keyframe, for adding one at the exact playhead without
     moving the value. One icon now, everywhere: the watch's own filled-on
     state is what says "this is animated" (see .tlwatch.on, app.css), and
     the timeline dock below still does everything the diamond did —
     dragging a keyframe to retime it, right-clicking one for easing or to
     delete it. What it does NOT reach is "add one here, unchanged" without
     first nudging the value and back; a real capability this drops, not a
     free simplification. */
  host.classList.toggle('f--animated', on);
  return host;
}
